import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import {
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { BoSection } from '../../shared/types'
import SectionRailSkeleton from './SectionRailSkeleton'

export interface SectionRailProps {
  sections: BoSection[]
  selectedId?: string
  onSelect: (id: string) => void
  onEdit?: (id: string) => void
  loading?: boolean
}

export default function SectionRail({
  sections,
  selectedId,
  onSelect,
  onEdit,
  loading = false,
}: SectionRailProps) {
  const { formatMessage } = useIntl()

  if (loading) {
    return <SectionRailSkeleton />
  }

  if (!sections.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {formatMessage({ id: 'avatarStudio.rail.noSections' })}
      </Typography>
    )
  }

  return (
    <List
      dense={true}
      aria-label={formatMessage({ id: 'avatarStudio.rail.ariaLabel' })}
      sx={{ py: 0 }}
    >
      {sections.map((section) => (
        // ListItem + secondaryAction, not a button inside a button: the edit control has to be a
        // sibling of the row, or it is invalid markup and unreachable by keyboard.
        <ListItem
          key={section.id}
          disablePadding={true}
          secondaryAction={
            onEdit && !section.unregistered ? (
              <Tooltip
                title={formatMessage(
                  { id: 'avatarStudio.rail.editSection' },
                  { section: section.displayName ?? section.id },
                )}
              >
                <IconButton
                  edge="end"
                  onClick={() => onEdit(section.id)}
                  aria-label={formatMessage(
                    { id: 'avatarStudio.rail.editSection' },
                    { section: section.displayName ?? section.id },
                  )}
                  data-testid={`avatar-section-edit-${section.id}`}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : undefined
          }
        >
          <ListItemButton
            selected={section.id === selectedId}
            onClick={() => onSelect(section.id)}
            data-testid={`avatar-section-${section.id}`}
          >
            <ListItemText
              primary={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{section.displayName ?? section.id}</span>
                  {section.unregistered && (
                    <Tooltip
                      title={formatMessage({
                        id: 'avatarStudio.section.unregisteredExplained',
                      })}
                    >
                      <WarningAmberOutlinedIcon
                        fontSize="inherit"
                        color="warning"
                        role="img"
                        tabIndex={0}
                        aria-label={formatMessage({
                          id: 'avatarStudio.section.unregistered',
                        })}
                      />
                    </Tooltip>
                  )}
                </Stack>
              }
              secondary={
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  component="span"
                >
                  <Chip
                    component="span"
                    size="small"
                    variant="outlined"
                    label={formatMessage({
                      id: section.unregistered
                        ? 'avatarStudio.section.unregistered'
                        : `avatarStudio.section.kind.${section.kind}`,
                    })}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="span"
                  >
                    {section.kind === 'color'
                      ? formatMessage(
                          { id: 'avatarStudio.rail.swatchCount' },
                          { count: (section.swatches ?? []).length },
                        )
                      : formatMessage(
                          { id: 'avatarStudio.rail.optionCount' },
                          { count: section.optionCount },
                        )}
                  </Typography>
                </Stack>
              }
              secondaryTypographyProps={{ component: 'span' }}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  )
}
